import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AdminOrganizationService } from '../../services/admin/admin-organization.service';
import { OrganizationModel, OrganizationFilter, SaveOrganizationPayload } from '../../models/school/organization.model';
import { ModalService } from '../../shared/modals/modal/modal.service';
import { ErrorModel } from '../../models/shared/error.model';
import { CountryModel } from '../../models/localization-and-pricing/country.model';
import { DropdownModel } from '../../models/component/dropdown.model';

@Component({
  selector: 'app-admin-organizations',
  templateUrl: './admin-organizations.component.html',
  styleUrls: ['./admin-organizations.component.css']
})
export class AdminOrganizationsComponent implements OnInit {

  organizations: OrganizationModel[] = [];
  searchQuery = '';
  sortByName: 1 | -1 = 1;
  countryFilter: string[] = [];
  isLoading = false;

  countryOptions: DropdownModel[] = [];

  readonly sortOptions: DropdownModel[] = [
    new DropdownModel('1', 'Name A→Z'),
    new DropdownModel('-1', 'Name Z→A')
  ];

  private collapsedGroups = new Set<string>();

  showCreateModal = false;
  editingOrganization: OrganizationModel = null;
  formName = '';
  formCountry = '';

  showDeleteModal = false;
  deletingOrganization: OrganizationModel = null;
  deleteConfirmInput = '';
  isDeleting = false;

  constructor(
    private organizationSv: AdminOrganizationService,
    private modalSv: ModalService,
    private router: Router
  ) {}

  ngOnInit() {
    const countryCodes = CountryModel.countriesForCurrentDomain();
    this.countryOptions = CountryModel.dropdownList(countryCodes);
    this.fetchOrganizations();
  }

  fetchOrganizations() {
    this.isLoading = true;
    const filter = new OrganizationFilter();
    filter.name = this.searchQuery || undefined;
    filter.sortByName = this.sortByName;
    this.organizationSv.getOrganizations(filter).subscribe(organizations => {
      this.organizations = organizations;
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
      this.modalSv.showAlertModal('Error', 'Could not load organizations. Please refresh the page.');
    });
  }

  onSearchChange(value: string) {
    this.searchQuery = value;
    this.fetchOrganizations();
  }

  setCountryFilter(keys: string[]) {
    this.countryFilter = keys;
  }

  get filteredOrganizations(): OrganizationModel[] {
    if (!this.countryFilter || this.countryFilter.length === 0) {
      return this.organizations;
    }
    return this.organizations.filter(o => this.countryFilter.indexOf(o.country) !== -1);
  }

  get isGroupedByCountry(): boolean {
    return !this.countryFilter || this.countryFilter.length === 0;
  }

  get groupedOrganizations(): { country: string, title: string, organizations: OrganizationModel[], totalSchools: number }[] {
    const groups = new Map<string, OrganizationModel[]>();
    for (const organization of this.filteredOrganizations) {
      const key = organization.country || '';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(organization);
    }
    return Array.from(groups.entries())
      .map(([country, organizations]) => ({
        country,
        title: this.countryTitle(country),
        organizations,
        totalSchools: organizations.reduce((sum, o) => sum + ((o.schoolIds && o.schoolIds.length) || 0), 0)
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }

  isGroupCollapsed(country: string): boolean {
    return this.collapsedGroups.has(country);
  }

  toggleGroup(country: string) {
    if (this.collapsedGroups.has(country)) {
      this.collapsedGroups.delete(country);
    } else {
      this.collapsedGroups.add(country);
    }
  }

  setSortBy(key: string) {
    this.sortByName = key === '-1' ? -1 : 1;
    this.fetchOrganizations();
  }

  countryTitle(code: string): string {
    return CountryModel.title(code);
  }

  openCreateModal() {
    this.editingOrganization = null;
    this.formName = '';
    this.formCountry = '';
    this.showCreateModal = true;
  }

  openEditModal(organization: OrganizationModel) {
    this.editingOrganization = organization;
    this.formName = organization.name;
    this.formCountry = organization.country;
    this.showCreateModal = true;
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.editingOrganization = null;
  }

  saveOrganization() {
    if (!this.formName || !this.formCountry) {
      this.modalSv.showAlertModal('Error', 'Please fill in all fields.');
      return;
    }
    const payload = new SaveOrganizationPayload();
    payload._id = this.editingOrganization ? this.editingOrganization._id : undefined;
    payload.name = this.formName;
    payload.country = this.formCountry;
    this.organizationSv.saveOrganization(payload).subscribe(() => {
      this.closeCreateModal();
      this.fetchOrganizations();
    }, (err: ErrorModel) => {
      this.modalSv.showTranslatedErrorModal(err);
    });
  }

  openDeleteModal(organization: OrganizationModel) {
    this.deletingOrganization = organization;
    this.deleteConfirmInput = '';
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.deletingOrganization = null;
  }

  get deleteConfirmValid(): boolean {
    return this.deletingOrganization && this.deleteConfirmInput.trim() === this.deletingOrganization.name;
  }

  confirmDeleteOrganization() {
    if (!this.deleteConfirmValid) {
      return;
    }
    this.isDeleting = true;
    this.organizationSv.deleteOrganization(this.deletingOrganization._id).subscribe(() => {
      this.isDeleting = false;
      this.closeDeleteModal();
      this.fetchOrganizations();
    }, (err: ErrorModel) => {
      this.isDeleting = false;
      this.modalSv.showTranslatedErrorModal(err);
    });
  }

  viewDetail(organization: OrganizationModel) {
    this.router.navigate(['admin', 'organizations', organization._id, 'detail']);
  }
}
